import type { LibraryConfig } from '../types/config.types';

/**
 * Configurazione centrale della libreria di componenti
 * 
 * Questo file contiene tutti i design tokens, regole di accessibilità,
 * configurazioni SEO e varianti predefinite.
 * 
 * MODIFICABILE: Puoi personalizzare qualsiasi valore qui per adattare
 * la libreria alle tue esigenze.
 */
export const libraryConfig: LibraryConfig = {
  /**
   * DESIGN TOKENS
   * Colori, spaziature, border-radius e shadows
   */
  designTokens: {
    colors: {
      primary: '#3b82f6',      // blue-500
      secondary: '#6366f1',    // indigo-500
      success: '#10b981',      // green-500
      error: '#ef4444',        // red-500
      warning: '#f59e0b',      // amber-500
      info: '#06b6d4',         // cyan-500
      neutral: '#6b7280',      // gray-500
    },
    spacing: {
      xs: '0.25rem',   // 4px
      sm: '0.5rem',    // 8px
      md: '1rem',      // 16px
      lg: '1.5rem',    // 24px
      xl: '2rem',      // 32px
      '2xl': '3rem',   // 48px
    },
    borderRadius: {
      none: '0',
      sm: '0.125rem',  // 2px
      md: '0.375rem',  // 6px
      lg: '0.5rem',    // 8px
      xl: '0.75rem',   // 12px
      full: '9999px',
    },
    shadows: {
      sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    },
  },

  /**
   * ACCESSIBILITÀ
   * Regole per contrast ratio, focus styles e ARIA defaults
   * Basato su WCAG 2.1 Level AA
   */
  accessibility: {
    contrastRatio: {
      normal: 4.5,  // Testo normale (< 18pt o < 14pt bold)
      large: 3,     // Testo grande (>= 18pt o >= 14pt bold)
    },
    focusStyles: {
      outline: '2px solid currentColor',
      outlineOffset: '2px',
    },
    ariaDefaults: {
      buttonRole: true,         // Aggiungi automaticamente role="button" ai button components
      linkRole: true,           // Aggiungi automaticamente role="link" ai link components
      autoAriaLabel: true,      // Genera aria-label automatici quando mancanti
    },
  },

  /**
   * SEO e OTTIMIZZAZIONE IMMAGINI
   * Configurazioni per immagini e meta tags
   * Segue le best practice di Google per Core Web Vitals
   */
  seo: {
    images: {
      defaultAlt: 'Immagine',
      lazyLoading: true,              // Usa loading="lazy" di default
      aspectRatioPreserve: true,      // Previeni layout shift
      formats: ['webp', 'avif', 'jpg'], // Formati supportati in ordine di preferenza
    },
    meta: {
      defaultTitle: 'Libreria Componenti',
      defaultDescription: 'Libreria di componenti React riutilizzabili con Tailwind CSS',
      titleTemplate: '%s | Libreria Componenti',
    },
  },

  /**
   * VARIANTI PREDEFINITE
   * Definisce le varianti disponibili e quella di default
   */
  variants: {
    default: 'primary',
    available: ['primary', 'secondary', 'success', 'error', 'warning', 'info', 'ghost', 'link'],
  },

  /**
   * DIMENSIONI PREDEFINITE
   * Definisce le size disponibili e quella di default
   */
  sizes: {
    default: 'md',
    available: ['xs', 'sm', 'md', 'lg', 'xl'],
  },
};

/**
 * Helper per accedere rapidamente ai design tokens
 */
export const tokens = libraryConfig.designTokens;
export const a11y = libraryConfig.accessibility;
export const seo = libraryConfig.seo;

